import { motion } from "framer-motion";
import styles from "./Home.module.css";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { t } from "../data/translations";
import projects from "../data/projects";
import AnimatedCounter from "../components/AnimatedCounter";
import ParticleField from "../components/ParticleField";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const techStack = [
  "React", "Node.js", "Express", "PostgreSQL", "MongoDB",
  "Python", "SQL", "Power BI", ".NET", "WordPress",
];

const featuredProjects = projects.slice(0, 3);

const Home = () => {
  const { lang } = useLang();

  const services = [
    {
      icon: "🌐",
      title: t("home.svcLogisticsTitle", lang),
      description: t("home.svcLogisticsDesc", lang),
      link: "/services",
    },
    {
      icon: "🛒",
      title: t("home.svcDataTitle", lang),
      description: t("home.svcDataDesc", lang),
      link: "/services",
    },
    {
      icon: "📈",
      title: t("home.svcWebTitle", lang),
      description: t("home.svcWebDesc", lang),
      link: "/services",
    },
  ];

  const stats = [
    { value: "10+", label: t("home.statYears", lang) },
    { value: "20+", label: t("home.statProjects", lang) },
    { value: "3", label: t("home.statAreas", lang) },
    { value: "100%", label: t("home.statClient", lang) },
  ];

  const processSteps = [
    { step: "01", title: t("home.processDiscoveryTitle", lang), description: t("home.processDiscoveryDesc", lang) },
    { step: "02", title: t("home.processAnalysisTitle", lang), description: t("home.processAnalysisDesc", lang) },
    { step: "03", title: t("home.processSolutionTitle", lang), description: t("home.processSolutionDesc", lang) },
    { step: "04", title: t("home.processResultsTitle", lang), description: t("home.processResultsDesc", lang) },
  ];

  const projectTitles = [
    t("projects.qrescueTitle", lang),
    t("projects.madsTitle", lang),
    t("projects.orderTitle", lang),
  ];
  const projectDescs = [
    t("projects.qrescueDesc", lang),
    t("projects.madsDesc", lang),
    t("projects.orderDesc", lang),
  ];

  return (
    <div className={styles.wrapper}>
      {/* Hero */}
      <motion.section
        className={styles.hero}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <ParticleField />
        <motion.span
          className={styles.badge}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {t("home.badge", lang)}
        </motion.span>
        <motion.h1
          className={styles.name}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          MyApple
        </motion.h1>
        <motion.p
          className={styles.tagline}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.6 }}
        >
          {t("home.tagline", lang)}
        </motion.p>
        <motion.p
          className={styles.sub}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          {t("home.sub", lang)}
        </motion.p>
        <motion.div
          className={styles.heroCtas}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
        >
          <Link to="/services" className={styles.cta}>
            {t("home.ctaServices", lang)}
          </Link>
          <Link to="/contact" className={styles.ctaOutline}>
            {t("home.ctaContact", lang)}
          </Link>
        </motion.div>

        <motion.div
          className={styles.scrollIndicator}
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <span className={styles.scrollArrow}>↓</span>
        </motion.div>
      </motion.section>

      {/* Introduction / About Summary */}
      <motion.section
        className={styles.introSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.div className={styles.introContent} variants={fadeUp}>
          <h2 className={styles.sectionTitle}>{t("home.whoWeAre", lang)}</h2>
          <div className={styles.introGrid}>
            <div className={styles.introText}>
              <p>
                {t("home.introP1", lang)}{" "}
                <strong>{t("home.introLogistics", lang)}</strong>,{" "}
                <strong>{t("home.introData", lang)}</strong>,{" "}
                {lang === "en" ? "and" : "και"}{" "}
                <strong>{t("home.introWeb", lang)}</strong>.
              </p>
              <p>{t("home.introP2", lang)}</p>
              <p>
                {t("home.introMission", lang)}{" "}
                <strong>{t("home.introMissionBold", lang)}</strong>{" "}
                {t("home.introMissionEnd", lang)}
              </p>
              <Link to="/about" className={styles.inlineLink}>
                {t("home.learnMore", lang)}
              </Link>
            </div>
            <div className={styles.statsGrid}>
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  className={styles.statCard}
                  variants={fadeUp}
                  whileHover={{ scale: 1.05, borderColor: "var(--accent-hover)" }}
                >
                  <span className={styles.statValue}>
                    <AnimatedCounter value={stat.value} />
                  </span>
                  <span className={styles.statLabel}>{stat.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* Services */}
      <motion.section
        className={styles.servicesSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.h2 className={styles.sectionTitle} variants={fadeUp}>
          {t("home.whatWeOffer", lang)}
        </motion.h2>
        <motion.p className={styles.sectionSubtitle} variants={fadeUp}>
          {t("home.servicesSubtitle", lang)}
        </motion.p>
        <div className={styles.servicesGrid}>
          {services.map((service, i) => (
            <motion.div
              key={i}
              className={styles.serviceCard}
              variants={fadeUp}
              whileHover={{ y: -6, boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}
            >
              <motion.div
                className={styles.serviceIcon}
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                {service.icon}
              </motion.div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <Link to={service.link} className={styles.serviceLink}>
                {t("home.serviceLearnMore", lang)}
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* How We Work */}
      <motion.section
        className={styles.processSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.h2 className={styles.sectionTitle} variants={fadeUp}>
          {t("home.howWeWork", lang)}
        </motion.h2>
        <motion.p className={styles.sectionSubtitle} variants={fadeUp}>
          {t("home.processSubtitle", lang)}
        </motion.p>
        <div className={styles.processGrid}>
          {processSteps.map((item, i) => (
            <motion.div
              key={i}
              className={styles.processCard}
              variants={fadeUp}
              whileHover={{ y: -4 }}
            >
              <motion.span
                className={styles.processStep}
                whileHover={{ scale: 1.2, opacity: 0.8 }}
              >
                {item.step}
              </motion.span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              {i < processSteps.length - 1 && (
                <span className={styles.processConnector}>→</span>
              )}
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Featured Projects */}
      <motion.section
        className={styles.portfolioSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.h2 className={styles.sectionTitle} variants={fadeUp}>
          {t("home.featuredWork", lang)}
        </motion.h2>
        <motion.p className={styles.sectionSubtitle} variants={fadeUp}>
          {t("home.featuredSubtitle", lang)}
        </motion.p>
        <div className={styles.projectsGrid}>
          {featuredProjects.map((proj, i) => (
            <motion.div
              key={i}
              className={styles.projectCard}
              variants={fadeUp}
              whileHover={{ y: -6, borderColor: "var(--accent)" }}
            >
              <h3>{projectTitles[i]}</h3>
              <p className={styles.projectDesc}>{projectDescs[i]}</p>
              <div className={styles.techTags}>
                {proj.tech.map((tech, j) => (
                  <motion.span
                    key={j}
                    whileHover={{ scale: 1.1 }}
                  >
                    {tech}
                  </motion.span>
                ))}
              </div>
              {proj.link && proj.link !== "#" && (
                <a
                  href={proj.link.startsWith("http") ? proj.link : `https://${proj.link}`}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.projectLink}
                >
                  {t("home.viewProject", lang)}
                </a>
              )}
            </motion.div>
          ))}
        </div>
        <motion.div className={styles.viewAllWrapper} variants={fadeUp}>
          <Link to="/portfolio" className={styles.viewAllLink}>
            {t("home.viewAll", lang)}
          </Link>
        </motion.div>
      </motion.section>

      {/* Tech Stack */}
      <motion.section
        className={styles.techSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.h2 className={styles.sectionTitle} variants={fadeUp}>
          {t("home.techTitle", lang)}
        </motion.h2>
        <motion.div className={styles.techList} variants={fadeUp}>
          {techStack.map((tech, i) => (
            <motion.span
              key={i}
              className={styles.techBadge}
              whileHover={{
                scale: 1.1,
                borderColor: "var(--accent)",
                color: "var(--accent-hover)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              {tech}
            </motion.span>
          ))}
        </motion.div>
      </motion.section>

      {/* Final CTA */}
      <motion.section
        className={styles.ctaSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.div className={styles.ctaContent} variants={fadeUp}>
          <h2>{t("home.ctaTitle", lang)}</h2>
          <p>{t("home.ctaDesc", lang)}</p>
          <div className={styles.ctaButtons}>
            <Link to="/contact" className={styles.cta}>
              {t("home.ctaStart", lang)}
            </Link>
            <Link to="/services" className={styles.ctaOutline}>
              {t("home.ctaSeeAll", lang)}
            </Link>
          </div>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default Home;
